import { useEffect, useState } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import isBetween from "dayjs/plugin/isBetween";
import customParseFormat from "dayjs/plugin/customParseFormat";
import {
  BarChartOutlined,
  CopyOutlined,
  ForwardOutlined,
  SearchOutlined,
  SendOutlined,
} from "@ant-design/icons";
import {
  Input,
  DatePicker,
  Button,
  Table,
  Tooltip,
  message,
  Avatar,
  Card,
  Select,
  Tag,
} from "antd";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import openNotification from "../../components/Notifications/openNotification";
import getDate from "../../untils/getDate";
import apiClient from "../../actions/api";
import { normalizeJobTypeCountry } from "../../config/countries";
import isEmpty from "../../validations/is-empty";
import { getJobDomain, getJobFaviconUrl } from "../../untils/jobPlatform";

const { RangePicker } = DatePicker;

dayjs.extend(customParseFormat);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isBetween);

const Analyze = ({ title }) => {
  // set title
  document.title = title;
  const navigate = useNavigate();

  const dateFormat = "YYYY-MM-DD";
  const user = useSelector((state) => state.auth.user);
  const themeMode = useSelector((state) => state.themeMode);
  const timezoneMode = useSelector((state) => state.timezoneMode);

  const [searchedData, setSearchedData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [debouncedSearchText, setDebouncedSearchText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalResults, setTotalResults] = useState(0);
  const [searchScheduled, setSearchScheduled] = useState(0);
  const [searchAlreadyBidded, setSearchAlreadyBidded] = useState(0);
  const [searchStartDate, setSearchStartDate] = useState(
    dayjs(getDate(dayjs())).tz(timezoneMode.timezoneMode, true),
  );
  const [searchEndDate, setSearchEndDate] = useState(
    dayjs(getDate(dayjs())).tz(timezoneMode.timezoneMode, true),
  );
  const [searchDays, setSearchDays] = useState(20);

  const columns = [
    {
      title: "#",
      key: "no",
      render: (_, record) => record.key,
    },
    {
      title: "Service",
      key: "job_service",
      render: (_, record) => {
        const jobUrl = record?.bid?.job_url;
        const domain = getJobDomain(jobUrl);
        return (
          <Tooltip placement="top" title={domain || "Unknown Service"}>
            <img
              className="service-icon"
              src={getJobFaviconUrl(jobUrl)}
              alt={domain ? `${domain} icon` : "Unknown service icon"}
            />
          </Tooltip>
        );
      },
    },
    {
      title: "Company Name",
      dataIndex: "company_name",
      key: "company_name",
      render: (text, record) =>
        record?.flag ? <span style={{ color: "red" }}>{text}</span> : text,
    },
    {
      title: "Company Role Title",
      dataIndex: "role_title",
      key: "role_title",
      render: (_, record) =>
        record?.flag ? (
          <span style={{ color: "red" }}>{record.bid.role_title}</span>
        ) : (
          record.bid.role_title
        ),
    },
    {
      title: "Bidders",
      dataIndex: "users",
      key: "users",
      render: (users) => (
        <Avatar.Group>
          {users?.map((item) => (
            <Tooltip title={item.name} placement="top" key={item._id}>
              <Avatar
                style={item._id === user._id ? { border: "3px solid red" } : {}}
                src={item.avatar}
              />
            </Tooltip>
          ))}
        </Avatar.Group>
      ),
    },
    {
      title: "Callers",
      dataIndex: "callers",
      key: "callers",
      render: (callers) => (
        <Avatar.Group>
          {callers?.map((item) => (
            <Tooltip title={item.name} placement="top" key={item._id}>
              <Avatar src={item.avatar} />
            </Tooltip>
          ))}
        </Avatar.Group>
      ),
    },
    {
      title: "Job actions",
      dataIndex: "job_url",
      key: "job_actions",
      render: (_, record) => (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Button
            type="primary"
            icon={<CopyOutlined />}
            onClick={() => handleCopy(record.bid.job_url)}
          >
            Copy
          </Button>
          <Button
            type="primary"
            icon={<ForwardOutlined />}
            onClick={() => handleRedirectJobSite(record.bid.job_url)}
          >
            Open Job Url
          </Button>
        </div>
      ),
    },
    {
      title: "Send",
      dataIndex: "send",
      key: "send",
      render: (_, record) => (
        <Button
          type="primary"
          icon={<SendOutlined />}
          disabled={record?.flag}
          onClick={() => handleSendBid(record.bid._id)}
        >
          Build resume
        </Button>
      ),
    },
  ];

  const fallbackCopyTextToClipboard = (text) => {
    const textArea = document.createElement("textarea");
    textArea.value = text;

    // Avoid scrolling to bottom
    textArea.style.position = "fixed";
    textArea.style.top = "0";
    textArea.style.left = "0";

    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      const successful = document.execCommand("copy");
      if (successful) {
        message.success("Copied to clipboard!");
      } else {
        console.warn("Fallback: Copy command was unsuccessful");
        message.error("Failed to copy!");
      }
    } catch (err) {
      console.error("Fallback: Unable to copy", err);
      message.error("Failed to copy!");
    }

    document.body.removeChild(textArea);
  };

  const handleRedirectJobSite = (jobUrl) => {
    window.open(jobUrl, "_blank");
  };

  const handleCopy = (copyText) => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(copyText)
        .then(() => {
          message.success("Copied to clipboard!");
        })
        .catch(() => {
          message.error("Failed to copy!");
        });
    } else {
      fallbackCopyTextToClipboard(copyText);
    }
  };

  const handleSendBid = (id) => {
    if (
      isEmpty(user?.template_url) ||
      isEmpty(user?.template_coverletter_url)
    ) {
      openNotification(
        "error",
        "Document templates required",
        "Please upload both a resume template and a cover letter template before generating a resume.",
      );
      return;
    }

    window.open(`/bid?bidID=${encodeURIComponent(id)}`, "_blank");
  };

  useEffect(() => {
    if (!isEmpty(user)) {
      getData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    user,
    searchStartDate,
    searchEndDate,
    searchDays,
    searchScheduled,
    searchAlreadyBidded,
    debouncedSearchText,
    currentPage,
    pageSize,
  ]);

  const getData = async () => {
    const originalStartTime = `${dayjs(searchStartDate).year()}-${String(
      dayjs(searchStartDate).month() + 1,
    ).padStart(2, "0")}-${String(dayjs(searchStartDate).date()).padStart(
      2,
      "0",
    )} 00:00:00`;

    const timeStartInUTC = dayjs(originalStartTime).tz(
      timezoneMode.timezoneMode,
      true,
    );
    const timeStartInGMTPlus9 = timeStartInUTC.tz(
      timezoneMode.timezoneMode,
      true,
    );

    const originalEndTime = `${dayjs(searchEndDate).year()}-${String(
      dayjs(searchEndDate).month() + 1,
    ).padStart(2, "0")}-${String(dayjs(searchEndDate).date()).padStart(
      2,
      "0",
    )} 23:59:59`;
    const timeEndInUTC = dayjs(originalEndTime).tz(
      timezoneMode.timezoneMode,
      true,
    );
    const timeEndInGMTPlus9 = timeEndInUTC.tz(timezoneMode.timezoneMode, true);

    const params = {
      searchStartDate: timeStartInGMTPlus9,
      searchEndDate: timeEndInGMTPlus9,
      job_type: normalizeJobTypeCountry(user.job_type),
      days: searchDays,
      email: user?.email,
      keyword: debouncedSearchText,
      scheduled: searchScheduled,
      alreadyBidded: searchAlreadyBidded,
      page: currentPage,
      pageSize,
    };

    setLoading(true);

    await apiClient
      .post("/bids/get-grouped", params)
      .then((res) => {
        const data = res.data.groupedResult || [];
        setTotalResults(res.data?.pagination?.total || 0);
        const dataWithKey = data.map((item, index) => ({
          ...item,
          key: (currentPage - 1) * pageSize + index + 1,
        }));

        if (!isEmpty(dataWithKey)) {
          setSearchedData(dataWithKey);
        } else {
          setSearchedData([]);
        }

        setLoading(false);
      })
      .catch((err) => {
        if (!isEmpty(err?.response?.data?.errors)) {
          err.response.data.errors.forEach((item) => {
            openNotification("error", "Validation Error!", item.msg);
          });
        }
        setLoading(false);
      });
  };

  return (
    <div
      className={`dashboardSection management-page analyze-page ${themeMode.themeMode ? "dashboard-dark" : ""}`}
    >
      <div className="dashboard-heading">
        <div>
          <h2 className="title">
            <span className="title-icon">
              <BarChartOutlined />
            </span>
            Analyze
          </h2>
          <p>Review job opportunities and bidding activity.</p>
        </div>
        <Tag className="result-badge">{totalResults} results</Tag>
      </div>
      <Card className="search-section" variant="borderless">
        <div className="filter-heading">
          <div>
            <SearchOutlined /> Search & filters
          </div>
          <span>Refine opportunities by activity and date</span>
        </div>
        <div className="filter-grid analyze-filter-grid">
          <div className="field">
            <div className="label">Keywords</div>
            <Input
              prefix={<SearchOutlined className="input-icon" />}
              placeholder="e.g. Frontend Engineer"
              onChange={(e) => setSearchText(e.target.value)}
              onPressEnter={() => {
                setCurrentPage(1);
                setDebouncedSearchText(searchText.trim());
              }}
              value={searchText}
            />
          </div>
          <div className="field">
            <div className="label">Scheduled</div>
            <Select
              value={searchScheduled}
              onChange={(value) => {
                setCurrentPage(1);
                setSearchScheduled(value);
              }}
              options={[
                {
                  value: 0,
                  label: "All",
                },
                {
                  value: 1,
                  label: "Scheduled",
                },
                {
                  value: 2,
                  label: "Not yet",
                },
              ]}
            />
          </div>
          <div className="field">
            <div className="label">Bid status</div>
            <Select
              value={searchAlreadyBidded}
              onChange={(value) => {
                setCurrentPage(1);
                setSearchAlreadyBidded(value);
              }}
              options={[
                {
                  value: 0,
                  label: "All",
                },
                {
                  value: 1,
                  label: "Bidded",
                },
                {
                  value: 2,
                  label: "Not yet",
                },
              ]}
            />
          </div>
          <div className="field">
            <div className="label">Date range</div>
            <RangePicker
              value={[searchStartDate, searchEndDate]}
              format={dateFormat}
              disabled={loading}
              onChange={(dates, dateStrings) => {
                if (dates) {
                  setCurrentPage(1);
                  setSearchStartDate(
                    dayjs(dateStrings[0]).tz(timezoneMode.timezoneMode, true),
                  );
                  setSearchEndDate(
                    dayjs(dateStrings[1]).tz(timezoneMode.timezoneMode, true),
                  );
                } else {
                  setSearchStartDate(
                    dayjs(getDate(dayjs())).tz(timezoneMode.timezoneMode, true),
                  );
                  setSearchEndDate(
                    dayjs(getDate(dayjs())).tz(timezoneMode.timezoneMode, true),
                  );
                }
              }}
            />
          </div>
          <div className="field">
            <div className="label">Period</div>
            <Select
              value={searchDays}
              onChange={(value) => {
                setCurrentPage(1);
                setSearchDays(value);
              }}
              options={[
                {
                  value: 10,
                  label: "10 Days",
                },
                {
                  value: 15,
                  label: "15 Days",
                },
                {
                  value: 20,
                  label: "20 Days",
                },
                {
                  value: 25,
                  label: "25 Days",
                },
                {
                  value: 30,
                  label: "30 Days",
                },
              ]}
            />
          </div>
        </div>
      </Card>
      <Card className="table-section" variant="borderless">
        <div className="table-heading">
          <div>
            <h3>Opportunity analysis</h3>
            <span>Jobs matching the selected criteria</span>
          </div>
          <Tag>{totalResults} records</Tag>
        </div>
        <Table
          columns={columns}
          dataSource={searchedData}
          loading={loading}
          size="middle"
          scroll={{ x: 1100 }}
          pagination={{
            current: currentPage,
            pageSize,
            total: totalResults,
            showSizeChanger: true,
            showTotal: (total) => `${total} opportunities`,
          }}
          onChange={(pagination) => {
            setCurrentPage(pagination.current);
            setPageSize(pagination.pageSize);
          }}
        />
      </Card>
    </div>
  );
};

export default Analyze;
